import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/lib/utils";

interface SliderProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    "value" | "defaultValue" | "onValueChange" | "onValueCommit"
  > {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  onValueCommit?: (value: number) => void;
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
}

const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      value,
      defaultValue,
      onValueChange,
      onValueCommit,
      min = 0,
      max = 100,
      step = 1,
      trackClassName,
      rangeClassName,
      thumbClassName,
      ...props
    },
    ref,
  ) => {
    const rootValue = value !== undefined ? [value] : undefined;
    const rootDefault = defaultValue !== undefined ? [defaultValue] : undefined;

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className,
        )}
        value={rootValue}
        defaultValue={rootDefault}
        min={min}
        max={max}
        step={step}
        onValueChange={
          onValueChange
            ? (next) => onValueChange(next[0] ?? min)
            : undefined
        }
        onValueCommit={
          onValueCommit
            ? (next) => onValueCommit(next[0] ?? min)
            : undefined
        }
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            "relative h-1.5 w-full grow overflow-hidden rounded-full bg-[hsl(var(--secondary))]",
            trackClassName,
          )}
        >
          <SliderPrimitive.Range
            className={cn(
              "absolute h-full rounded-full bg-[hsl(var(--primary))]",
              rangeClassName,
            )}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={cn(
            "block h-4 w-4 rounded-full border-2 border-[hsl(var(--primary))] bg-[hsl(var(--card))] shadow",
            "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50",
            thumbClassName,
          )}
        />
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = "Slider";

export { Slider };
